import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
} from '@tanstack/react-query';
import config from 'src/config';
import ApiResponse from './ApiResponse';
import ApiError from './ApiError';
import { useToast } from 'src/context/ToastContext';
import { useRef } from 'react';

interface UseApiParams<TBody, T, R = T, TContext = unknown> {
  endpoint: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  options?: UseMutationOptions<ApiResponse<R>, ApiError, TBody, TContext>;
  transformResponse?: (data: T) => R;
  transformRequest?: (data: TBody) => FormData | TBody;
  errorHandler?: (e: ApiError) => Error;
  showSuccessToast?: boolean;
  successMessage?: string;
  showErrorToast?: boolean;
  errorMessage?: string;
}

type MutationResultWithAbort<T, E, V, TContext = unknown> = UseMutationResult<
  T,
  E,
  V,
  TContext
> & {
  abort: () => void;
  // Add backwards compatibility for isLoading
  isLoading: boolean;
};

const useMutationApi = <TBody, T = unknown, R = T, TContext = unknown>({
  endpoint,
  method,
  options,
  transformResponse,
  transformRequest,
  errorHandler,
  showSuccessToast = true,
  successMessage = 'Success',
  showErrorToast = true,
  errorMessage = 'Error occurred',
}: UseApiParams<TBody, T, R, TContext>): MutationResultWithAbort<
  ApiResponse<R>,
  ApiError,
  TBody,
  TContext
> => {
  const { showToast } = useToast();
  const abortControllerRef = useRef<AbortController | null>(null);

  const mutation = useMutation<ApiResponse<R>, ApiError, TBody, TContext>({
    ...options,
    mutationFn: async (body: TBody) => {
      // Abort previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new controller for this request
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      const transformedData = transformRequest ? transformRequest(body) : body;
      const isFormData = transformedData instanceof FormData;

      try {
        const response = await fetch(`${config.apiBaseUrl}/${endpoint}`, {
          method,
          headers: isFormData
            ? undefined
            : { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: isFormData ? transformedData : JSON.stringify(transformedData),
          signal,
        });

        if (!response.ok) {
          const errorResponse: {
            status?: number;
            message?: string;
            erpSystemName?: string;
            erpErrorDetails?: string;
            data?: unknown; // Preserve full error response data
          } = await response.json();
          const apiError = new ApiError(
            errorResponse.status || response.status,
            errorResponse.message || 'An error occurred',
            errorResponse.erpSystemName,
            errorResponse.erpErrorDetails,
          );
          // Attach full error response for error handling
          (apiError as unknown as { responseData?: unknown }).responseData =
            errorResponse;
          if (errorHandler) throw errorHandler(apiError);
          throw apiError;
        }

        // Handle 204 No Content responses (no body)
        if (response.status === 204) {
          return {
            data: null,
            success: true,
          } as unknown as ApiResponse<R>;
        }

        const responseData: ApiResponse<T> = await response.json();

        if (transformResponse) {
          return {
            ...responseData,
            data: transformResponse(responseData.data),
          };
        }

        return responseData as unknown as ApiResponse<R>;
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          throw new Error('Request cancelled');
        }
        throw error;
      }
    },
    onSuccess: (...args) => {
      if (showSuccessToast) {
        showToast({
          message: successMessage,
          title: 'Success',
          color: 'success',
          icon: 'check',
        });
      }
      options?.onSuccess?.(...args);
    },
    onError: (error, variables, context) => {
      if (showErrorToast) {
        const backendMessage =
          error instanceof ApiError
            ? error.message
            : (error as Error)?.message || errorMessage;

        showToast({
          message: backendMessage,
          title: 'Error',
          color: 'error',
          icon: 'error',
        });
      }

      options?.onError?.(error, variables, context);
    },
  });

  return {
    ...mutation,
    // Backwards compatibility: map isPending to isLoading
    isLoading: mutation.isPending,
    abort: () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    },
  };
};

export default useMutationApi;
